import * as React from 'react';
import {
  CcfNavigationIcon,
  CcfTable,
  CcfTypography,
  RowDataStructure,
  useTranslator,
} from '@nice-ccf/ui-controls';
import CcfQueueCounterChannelsStyles from './ccf-queue-counter-channels.styles';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import CcfSkillDetails from '../ccf-skill-details/ccf-skill-details';
import { CHANNEL_ICON_NAME, iconList } from '../ccf-icon/ccf-icon-list';
import { convertMinutestoHours } from '../../util/uiValidationUtils';
import { CcfTranslationKey } from '@nice-ccf/i18n';
import { MediaTypeId } from '@nice-ccf/core-sdk';

/**
 * Interface for Queue Counter
 */
interface CcfQueueCounterChannelsProps {
  isAppSpace?: boolean;
}

/**
 * Component displays queue counter channel details
 * @returns queue counter header and table
 * @example <CcfQueueCounterChannels/>
 */
export function CcfQueueCounterChannels({
  isAppSpace,
}: CcfQueueCounterChannelsProps) {
  const theme = useTheme();
  const isSmView = useMediaQuery(theme.breakpoints.down('xl'));
  const isMdView = useMediaQuery('(max-width:575px)'); // Need to remove it after breakpoints added into theme file
  const queueCounterStyles = CcfQueueCounterChannelsStyles(
    theme,
    isSmView,
    isAppSpace
  );
  const [translate] = useTranslator();
  const queueSkillDetails = {
    longestPhoneWait: 112,
    longestWorkItemWait: 0,
    longestVoiceMailWait: 112,
    longestDigitalWait: 210,
    totalPhoneContacts: 3,
    totalWorkItemContacts: 0,
    totalVoiceMailContacts: 2,
    totalDigitalContacts: 9,
    totalAllContacts: 13,
  };
  const [rowItemClicked, setRowItemClicked] = useState(0);
  const [skillDetailsScreen, setSkillDetailsScreen] = useState(false);
  const tableHeaders = isMdView
    ? [translate('channel'), translate('contacts'), '']
    : [
        translate('channel'),
        translate('contactsInQueue'),
        translate('wait'),
        '',
      ];
  const colWidths = ['40%', '25%', '25%', '10%'];
  const colWidthsMdView = ['60%', '30%', '10%'];

  /**
   * Used to get table rows data
   * @example
   * getTableHeaders()
   */
  const createRowData = (
    key: number,
    icon: string,
    iconString: string,
    totalContacts: number,
    longestWait: string
  ) => {
    return {
      key: key,
      channel: iconString,
      totalcontacts: totalContacts,
      longestwait: longestWait,
      data: [
        <Box style={queueCounterStyles.containerBox}>
          {iconList[icon]('20px', { htmlColor: theme.palette.text.noteLabel })}
          <CcfTypography
            variant="h4"
            sx={queueCounterStyles.iconHeaderClasses}
            translationKey={iconString as CcfTranslationKey}
            title={translate(iconString as CcfTranslationKey)}
            aria-label={translate(iconString as CcfTranslationKey)}
          />
        </Box>,
        <CcfTypography
          variant="h4"
          title={totalContacts.toString()}
          aria-label={totalContacts.toString()}
        >
          {totalContacts}
        </CcfTypography>,
        <CcfTypography
          variant="h4"
          title={longestWait.toString()}
          aria-label={longestWait}
        >
          {longestWait === '0s' ? '-' : longestWait}
        </CcfTypography>,
        <CcfNavigationIcon
          viewBox="0 0 18 18"
          sx={queueCounterStyles.navigationIcon}
        />,
      ],
    };
  };
  const structuredRowData: RowDataStructure[] = [
    createRowData(
      MediaTypeId.Digital,
      CHANNEL_ICON_NAME.DIGITAL,
      'digital',
      queueSkillDetails.totalDigitalContacts,
      convertMinutestoHours(queueSkillDetails.longestDigitalWait)
    ),
    createRowData(
      MediaTypeId.PhoneCall,
      CHANNEL_ICON_NAME.VOICE,
      'inboundCall',
      queueSkillDetails.totalPhoneContacts,
      convertMinutestoHours(queueSkillDetails.longestPhoneWait)
    ),
    createRowData(
      MediaTypeId.WorkItem,
      CHANNEL_ICON_NAME.WORK_ITEM,
      'workitem',
      queueSkillDetails.totalWorkItemContacts,
      convertMinutestoHours(queueSkillDetails.longestWorkItemWait)
    ),
    createRowData(
      MediaTypeId.VoiceEmail,
      CHANNEL_ICON_NAME.VOICEMAIL,
      'voicemail',
      queueSkillDetails.totalVoiceMailContacts,
      convertMinutestoHours(queueSkillDetails.longestVoiceMailWait)
    ),
  ];

  /**
   * Used to get table row data based on resolution
   * @example
   * getRowData()
   */
  const getRowData = () => {
    return isMdView
      ? structuredRowData.filter((rowDetails: RowDataStructure) =>
          rowDetails.data.splice(2, 1)
        )
      : structuredRowData;
  };

  /**
   * Used to get column Widths
   * @example
   * const getColWidths()
   */
  const getColWidths = () => {
    return isMdView ? colWidthsMdView : colWidths;
  };

  /**
   * Used to handle rowclick of table
   * @example
   * const onClick = handleTableRow
   */
  const onRowClick = (rowData: RowDataStructure) => {
    rowData.key && setRowItemClicked(rowData.key);
    setSkillDetailsScreen && setSkillDetailsScreen(true);
  };
  return !skillDetailsScreen ? (
    <Box sx={queueCounterStyles.channelDetails}>
      <Box sx={queueCounterStyles.tableDetails}>
        <CcfTable
          tableStyle={queueCounterStyles.tableStyle}
          colWidths={getColWidths()}
          headersData={tableHeaders}
          rowsData={getRowData()}
          rowStyle={queueCounterStyles.tableBodyRow}
          headerStyle={queueCounterStyles.tableHeadRow}
          containerStyle={queueCounterStyles.customTableContainer}
          onRowClick={onRowClick}
          isHideSortIcon={false}
        />
      </Box>
    </Box>
  ) : (
    <CcfSkillDetails
      setSkillDetailsScreen={setSkillDetailsScreen}
      isAppSpace={isAppSpace}
      rowItemClicked={rowItemClicked}
    />
  );
}
